import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    Plus, Search, Wifi, Edit, Trash2, MapPin, 
    Network, Check, Upload, FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AccessPoints() {
    const { isAdmin } = useAuth();
    const [accessPoints, setAccessPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [editingAP, setEditingAP] = useState(null);
    const [importData, setImportData] = useState('');
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        location: '',
        ip_address: '',
        mac_address: '',
        ssid: '',
        brand: '',
        model: '',
        notes: '',
    });

    useEffect(() => {
        loadAccessPoints();
    }, []);

    const loadAccessPoints = async (searchTerm = '') => {
        try {
            const token = localStorage.getItem('token');
            const url = searchTerm 
                ? `${API_URL}/api/access-points?search=${encodeURIComponent(searchTerm)}`
                : `${API_URL}/api/access-points`;
            
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAccessPoints(data);
            }
        } catch (error) {
            console.error('Load error:', error);
            toast.error('Access Point listesi yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        loadAccessPoints(value);
    };

    const openCreateDialog = () => {
        setEditingAP(null);
        setFormData({
            name: '',
            location: '',
            ip_address: '',
            mac_address: '',
            ssid: '',
            brand: '',
            model: '',
            notes: '',
        });
        setDialogOpen(true);
    };

    const openEditDialog = (ap) => {
        setEditingAP(ap);
        setFormData({
            name: ap.name || '',
            location: ap.location || '',
            ip_address: ap.ip_address || '',
            mac_address: ap.mac_address || '',
            ssid: ap.ssid || '',
            brand: ap.brand || '',
            model: ap.model || '',
            notes: ap.notes || '',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.location) {
            toast.error('AP adı ve konum zorunludur');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = editingAP 
                ? `${API_URL}/api/access-points/${editingAP.id}`
                : `${API_URL}/api/access-points`;
            
            const res = await fetch(url, {
                method: editingAP ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (res.ok) {
                toast.success(editingAP ? 'Access Point güncellendi' : 'Access Point eklendi');
                setDialogOpen(false);
                loadAccessPoints(search);
            } else {
                toast.error('İşlem başarısız');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleDelete = async (ap) => {
        if (!window.confirm(`"${ap.name}" access point'i silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/access-points/${ap.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (res.ok) {
                toast.success('Access Point silindi');
                loadAccessPoints(search);
            } else {
                toast.error('Silme işlemi başarısız');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <div className="grid gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="access-points-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Access Point Yönetimi</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Toplam {accessPoints.length} access point kayıtlı
                    </p>
                </div>
                {isAdmin && (
                    <Button 
                        onClick={openCreateDialog}
                        className="bg-orwion-green hover:bg-orwion-green-hover" 
                        data-testid="create-ap-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Access Point
                    </Button>
                )}
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="AP adı, konum, IP, MAC veya SSID ile ara..."
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="pl-10"
                            data-testid="search-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Access Points List */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-lg shadow">
                    <thead>
                        <tr className="bg-slate-50 border-b">
                            <th className="text-left p-4 font-medium text-slate-600">AP Adı</th>
                            <th className="text-left p-4 font-medium text-slate-600">Konum</th>
                            <th className="text-left p-4 font-medium text-slate-600">IP Adresi</th>
                            <th className="text-left p-4 font-medium text-slate-600">MAC Adresi</th>
                            <th className="text-left p-4 font-medium text-slate-600">SSID</th>
                            <th className="text-left p-4 font-medium text-slate-600">Marka/Model</th>
                            <th className="text-left p-4 font-medium text-slate-600">Durum</th>
                            <th className="text-right p-4 font-medium text-slate-600">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accessPoints.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="p-8 text-center text-slate-500">
                                    <Wifi className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Henüz access point kaydı yok</p>
                                </td>
                            </tr>
                        ) : (
                            accessPoints.map((ap) => (
                                <tr key={ap.id} className="border-b hover:bg-slate-50">
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <Wifi className="w-4 h-4 text-blue-500" />
                                            <span className="font-medium">{ap.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-1 text-slate-600">
                                            <MapPin className="w-3 h-3" />
                                            {ap.location}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                                            {ap.ip_address || '-'}
                                        </code>
                                    </td>
                                    <td className="p-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                                            {ap.mac_address || '-'}
                                        </code>
                                    </td>
                                    <td className="p-4">
                                        <Badge variant="outline" className="bg-blue-50">
                                            {ap.ssid || '-'}
                                        </Badge>
                                    </td>
                                    <td className="p-4 text-sm text-slate-600">
                                        {ap.brand && ap.model 
                                            ? `${ap.brand} ${ap.model}` 
                                            : ap.brand || ap.model || '-'}
                                    </td>
                                    <td className="p-4">
                                        <Badge className={ap.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                            {ap.status === 'active' ? 'Aktif' : 'Pasif'}
                                        </Badge>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                onClick={() => openEditDialog(ap)}
                                                data-testid={`edit-ap-${ap.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="text-red-500 hover:text-red-600"
                                                onClick={() => handleDelete(ap)}
                                                data-testid={`delete-ap-${ap.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingAP ? 'Access Point Düzenle' : 'Yeni Access Point Ekle'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="grid grid-cols-2 gap-4 py-4">
                        <div className="space-y-2">
                            <Label>AP Adı *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Örn: Lobi AP 1"
                                data-testid="ap-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Konum *</Label>
                            <Input
                                value={formData.location}
                                onChange={(e) => setFormData({...formData, location: e.target.value})}
                                placeholder="Örn: 1. Kat Koridor"
                                data-testid="ap-location-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>IP Adresi</Label>
                            <Input
                                value={formData.ip_address}
                                onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                                placeholder="Örn: 192.168.1.50"
                                data-testid="ap-ip-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>MAC Adresi</Label>
                            <Input
                                value={formData.mac_address}
                                onChange={(e) => setFormData({...formData, mac_address: e.target.value})}
                                placeholder="Örn: AA:BB:CC:DD:EE:FF"
                                data-testid="ap-mac-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>SSID</Label>
                            <Input
                                value={formData.ssid}
                                onChange={(e) => setFormData({...formData, ssid: e.target.value})}
                                placeholder="Örn: Hotel_WiFi"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Marka</Label>
                            <Input
                                value={formData.brand}
                                onChange={(e) => setFormData({...formData, brand: e.target.value})}
                                placeholder="Örn: Ubiquiti, Cisco"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Model</Label>
                            <Input
                                value={formData.model}
                                onChange={(e) => setFormData({...formData, model: e.target.value})}
                                placeholder="Örn: UAP-AC-PRO"
                            />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Notlar</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                placeholder="Ek notlar..."
                                rows={2}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            onClick={handleSubmit}
                            className="bg-orwion-green hover:bg-orwion-green-hover"
                            data-testid="save-ap-btn"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            {editingAP ? 'Güncelle' : 'Kaydet'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default AccessPoints;
